import { Chat, Help as HelpIcon } from '@tunasong/icons'
import {
  Button,
  Dialog,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuContent,
  MenuItem,
  MenuTrigger,
  ShortcutBox,
  shortcuts,
  useTunaMediaQuery,
} from '@tunasong/ui-lib'
import { isSupportChatAvailable, isSupportChatEnabled, openSupportChat } from '@tunasong/vendor'
import type { FC } from 'react'
import { useId, useState } from 'react'
import styles from './help-menu.module.css'

export interface KeyboardShortcutsMenuItemProps {
  /** Show the shortcuts dialog. The menu's owner renders it: anything inside the menu unmounts when it closes. */
  onShow(): void
}

export const KeyboardShortcutsMenuItem: FC<KeyboardShortcutsMenuItemProps> = ({ onShow }) => (
  <MenuItem onClick={onShow}>
    <ListItemIcon>
      <HelpIcon />
    </ListItemIcon>
    <ListItemText primary="Keyboard shortcuts" />
  </MenuItem>
)

const unavailableReason = 'Support chat is unavailable — it may be blocked by your browser.'

/**
 * Opens the Crisp support chat (the floating bubble is hidden on entity pages). Not rendered when
 * support chat is off for this build. When Crisp has not booted (e.g. blocked), the item stays
 * listed, disabled, with the reason as visible secondary text and its accessible description.
 */
export const ContactSupportMenuItem: FC = () => {
  const labelId = useId()
  const reasonId = useId()
  if (!isSupportChatEnabled()) {
    return null
  }
  const disabled = !isSupportChatAvailable()

  return (
    <MenuItem
      className={styles.item}
      disabled={disabled}
      aria-disabled={disabled || undefined}
      aria-labelledby={labelId}
      aria-describedby={disabled ? reasonId : undefined}
      onClick={() => openSupportChat()}
    >
      <ListItemIcon className={styles.icon}>
        <Chat />
      </ListItemIcon>
      <ListItemText
        className={styles.text}
        primary={
          <span id={labelId} className={styles.label}>
            Contact support
          </span>
        }
        secondary={
          disabled ? (
            <span id={reasonId} className={styles.reason}>
              {unavailableReason}
            </span>
          ) : undefined
        }
      />
    </MenuItem>
  )
}

export interface KeyboardShortcutsDialogProps {
  open: boolean
  onClose(): void
}

export const KeyboardShortcutsDialog: FC<KeyboardShortcutsDialogProps> = ({ open, onClose }) => (
  <Dialog open={open} onClose={onClose} title="Keyboard Shortcuts">
    {Object.values(shortcuts).map((s, idx) => (
      <ShortcutBox key={idx} shortcut={s} />
    ))}
  </Dialog>
)

export interface HelpMenuButtonProps {
  /** Class for the trigger: the host styles it (the sidebar's 32px ghost icon button) */
  className?: string
}

/**
 * "Help" for the app, not for any one song: Keyboard shortcuts and Contact support. Lives in the left sidebar footer.
 * Keyboard shortcuts is left out on touch-only devices (`pointer: coarse`), where it does not apply, and with nothing
 * left to offer the button is not rendered.
 */
export const HelpMenuButton: FC<HelpMenuButtonProps> = ({ className }) => {
  const [shortcutsOpen, setShortcutsOpen] = useState(false)
  const isCoarsePointer = useTunaMediaQuery('(pointer: coarse)')
  const showShortcuts = !isCoarsePointer

  if (!showShortcuts && !isSupportChatEnabled()) {
    return null
  }

  return (
    <>
      <Menu>
        <MenuTrigger
          render={
            <button type="button" className={className} aria-label="Help" title="Help">
              <HelpIcon />
            </button>
          }
        />
        <MenuContent>
          {showShortcuts ? <KeyboardShortcutsMenuItem onShow={() => setShortcutsOpen(true)} /> : null}
          <ContactSupportMenuItem />
        </MenuContent>
      </Menu>
      {showShortcuts ? <KeyboardShortcutsDialog open={shortcutsOpen} onClose={() => setShortcutsOpen(false)} /> : null}
    </>
  )
}

export interface ContactSupportButtonProps {
  className?: string
}

/**
 * "Contact support" as a button, for the profile page: on mobile there is no sidebar footer, and the account button
 * opens the profile. Same rules as the menu item: hidden when support chat is off for the build, disabled with the
 * reason as its description when Crisp has not booted.
 */
export const ContactSupportButton: FC<ContactSupportButtonProps> = ({ className }) => {
  const reasonId = useId()
  if (!isSupportChatEnabled()) {
    return null
  }
  const disabled = !isSupportChatAvailable()

  return (
    <div className={className}>
      <Button
        variant="outlined"
        startIcon={<Chat />}
        disabled={disabled}
        aria-describedby={disabled ? reasonId : undefined}
        onClick={() => openSupportChat()}
      >
        Contact support
      </Button>
      {disabled ? (
        <p id={reasonId} className={styles.reason}>
          {unavailableReason}
        </p>
      ) : null}
    </div>
  )
}
